import * as React from "react";
import { Stack } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { useOrderRiskPreview } from "@/hooks/queries/useOrderPreview";
import type { InstrumentSummary } from "./InstrumentSearch";
import type { OrderTicketValues } from "./OrderTicket";

interface RiskPreviewPayload {
  decision: string;
  warnings: string[];
  failed_rules: string[];
  review_required: boolean;
  estimated_risk_level: string;
}

interface RiskSummaryCardProps {
  instrument: InstrumentSummary | null;
  preview: OrderTicketValues | null;
}

/**
 * Backend v2.1 (Sprint 4.5) `POST /api/v1/orders/preview` - runs the
 * REAL Risk Engine against the Order Ticket's own already-entered values
 * without ever submitting an order (strictly read-only, test-proven
 * backend-side). Auto-refetches whenever the ticket's preview values
 * change, the same pattern `QuoteCard`/`InstrumentSearch` already use
 * for instrument-selection-driven data. This never places a trade; the
 * Submit button remains permanently disabled in `OrderTicket`.
 */
export function RiskSummaryCard({ instrument, preview }: RiskSummaryCardProps): React.JSX.Element {
  const previewQuery = useOrderRiskPreview(
    instrument && preview
      ? {
          instrumentToken: instrument.instrument_token,
          side: preview.side,
          quantity: preview.quantity,
          price: preview.price,
        }
      : null,
  );

  if (!instrument || !preview) {
    return (
      <StatusCard title="Risk Summary">
        <EmptyState message="Fill in the Order Ticket and click Preview to see a risk evaluation here." />
      </StatusCard>
    );
  }

  return (
    <StatusCard title="Risk Summary">
      <QueryStateBoundary query={previewQuery}>
        {(payload) => {
          const risk = payload as unknown as RiskPreviewPayload;
          return (
            <Stack spacing={1.5}>
              <MetricCard label="Risk Decision" value={risk.decision} />
              <MetricCard label="Risk Level" value={risk.estimated_risk_level} />
              <MetricCard label="Review Required" value={risk.review_required} />
              <MetricCard
                label="Warnings"
                value={risk.warnings.length > 0 ? risk.warnings.join(", ") : "None"}
              />
              <MetricCard
                label="Failed Rules"
                value={risk.failed_rules.length > 0 ? risk.failed_rules.join(", ") : "None"}
              />
            </Stack>
          );
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
